/**
 * Peças visuais compartilhadas pelos cartões e gráficos do painel de publicações
 * (/publications/painel): moldura do cartão, estatística rotulada e o tema dos eixos
 * dos gráficos recharts, tudo por tokens do tema.
 */
import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/utils/cn'

export type PainelTone = 'default' | 'primary' | 'destructive' | 'muted'

export const painelToneClass: Record<PainelTone, string> = {
  default: 'text-foreground',
  primary: 'text-primary',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground'
}

interface PainelCardProps {
  title: ReactNode
  icon: LucideIcon
  /** Conteúdo à direita do título (período, total, navegação). */
  action?: ReactNode
  children: ReactNode
  className?: string
}

export function PainelCard({ title, icon: Icon, action, children, className }: PainelCardProps) {
  return (
    <Card className={cn('min-w-0', className)}>
      <CardHeader>
        <CardTitle className="flex min-w-0 items-center gap-2 text-sm font-semibold">
          <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <h3 className="min-w-0 break-words">{title}</h3>
        </CardTitle>
        {action && (
          <CardAction className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            {action}
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-4">{children}</CardContent>
    </Card>
  )
}

interface PainelStatProps {
  label: string
  value: ReactNode
  tone?: PainelTone
  hint?: string
}

export function PainelStat({ label, value, tone = 'default', hint }: PainelStatProps) {
  return (
    <div className="min-w-0 text-center">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('text-lg font-semibold tabular-nums', painelToneClass[tone])}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** Faixa de estatísticas no rodapé de um cartão, separada por borda. */
export function PainelStatRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 gap-3 border-t border-border pt-4 md:grid-cols-4', className)}>
      {children}
    </div>
  )
}

export function PainelEmpty({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-muted-foreground">
      <Icon className="size-6" aria-hidden="true" />
      <p>{children}</p>
    </div>
  )
}

/** Caixa do tooltip customizado dos gráficos recharts. */
export function ChartTooltipBox({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-popover p-3 text-sm text-popover-foreground shadow-md">
      {children}
    </div>
  )
}

/** Eixos e grade dos gráficos recharts por variáveis do tema. */
export const chartAxisProps = {
  tick: { fontSize: 12, fill: 'var(--muted-foreground)' },
  stroke: 'var(--border)'
} as const

export const chartGridStroke = 'var(--border)'
